import { ChangeEvent, useEffect, useRef, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import { useSearchParamsHandler } from "../../hooks/useSearchParamsHandler.ts";
import { LOADING_CIRCLE } from "./loading/LoadingCircle.tsx";

function useDebounce<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debouncedValue;
}

const SearchBar = ({ placeholder = "Suche" }: { placeholder?: string }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { set, get, remove, removeOther } = useSearchParamsHandler("search");
  // local state for typing
  const [value, setValue] = useState(get()[0] || "");
  const debouncedValue = useDebounce(value, 750);

  // URL debounced local
  useEffect(() => {
    if (debouncedValue) {
      removeOther("page");
      set(debouncedValue);
    } else {
      remove();
    }
  }, [debouncedValue]);

  // global hotkey
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <label className="input input-sm w-full bg-base-200">
      <IconSearch className="inline-block size-4" />
      <input
        ref={inputRef}
        type="search"
        className="grow"
        inputMode="search"
        name="search"
        placeholder={placeholder}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setValue(e.currentTarget.value)
        }
      />
      <div className="hidden lg:block">
        <kbd className="kbd kbd-sm">⌘</kbd>
        <kbd className="kbd kbd-sm">K</kbd>
      </div>
      {debouncedValue !== value && LOADING_CIRCLE}
    </label>
  );
};

export default SearchBar;
